import { Trans } from '@lingui/macro'
import {
  Button,
  Card,
  Grid,
  Icon,
  IconButton,
  TextField
} from '@mui/material'
import { useFormikContext } from 'formik'
import React, { useEffect, useState } from 'react'

import { languages } from 'translation/I18nConnectedProvider'

const searchForOccurrence = ({ data, searchString, objectIdToName }) => {
  const found = {}

  const checkTextForSearchString = (string, item, section) => {
    if (
      string &&
      String(string).toLowerCase().includes(String(searchString).toLowerCase())
    ) {
      found[item.id] = {
        id: item.id,
        section
      }
    }
  }

  const checkConditionForReferences = (condition, item, section) => {
    const { sfField } = condition
    if (sfField) {
      checkTextForSearchString(sfField, item, section)
    }
  }

  const checkElementForReferences = (item, sectionIndex) => {
    const textKeys = ['title', 'helpText', 'tooltip']
    textKeys.forEach(key => {
      languages.forEach(lang => {
        if (item?.[key]?.[lang]) {
          checkTextForSearchString(item[key][lang], item, sectionIndex)
        }
      })
    })

    if (item?.typeProps?.html) {
      languages.forEach(lang => {
        if (
          item.typeProps.html[lang] ||
          item.typeProps.html.portalTranslationId
        ) {
          checkTextForSearchString(
            item.typeProps.html[lang],
            item,
            sectionIndex
          )
        }
      })
    }

    const typePropsKeys = ['imgLink', 'tag', 'url']

    if (item.typeProps) {
      typePropsKeys.forEach(key => {
        if (item.typeProps[key]) {
          checkTextForSearchString(item.typeProps[key], item, sectionIndex)
        }
      })
      if (item.typeProps.connectedTo) {
        item.typeProps.connectedTo.forEach(connObj => {
          const fieldName = connObj.connectedField?.label
          const objId = connObj.connectedObject
          if (objId) {
            checkTextForSearchString(objectIdToName[objId], item, sectionIndex)
          }
          checkTextForSearchString(fieldName, item, sectionIndex)
        })
      }
    }

    if (item.conditions) {
      item.conditions.forEach(condition => {
        if (condition.conditions) {
          condition.conditions.forEach(subcondition => {
            checkConditionForReferences(subcondition, item, sectionIndex)
          })
        } else {
          checkConditionForReferences(condition, item, sectionIndex)
        }
      })
    }

    const typeProps = item.typeProps || {}
    const { content = [], options = [] } = typeProps
    switch (item.elementType) {
      case 'textWithReferences':
        content.forEach(item => {
          const { type, text } = item
          if (type === 'text') {
            languages.forEach(lang => {
              if (text?.[lang]) {
                checkTextForSearchString(text[lang], item, sectionIndex)
              }
            })
          }
        })
        break
      case 'picklist':
        options.forEach(option => {
          languages.forEach(lang => {
            if (option?.title?.[lang]) {
              checkTextForSearchString(option.title[lang], item, sectionIndex)
            }
          })
          checkTextForSearchString(option.apiValue, item, sectionIndex)
          const { connectedField } = option
          if (connectedField) {
            checkTextForSearchString(connectedField.name, item, sectionIndex)
          }
        })
        break
      case 'objectives':
        languages.forEach(lang => {
          if (
            item?.needsHelpText?.[lang] ||
            item?.needsHelpText?.portalTranslationId
          ) {
            checkTextForSearchString(
              item.needsHelpText?.[lang],
              item,
              sectionIndex
            )
          }
          if (
            item?.outcomesHelpText?.[lang] ||
            item?.outcomesHelpText?.portalTranslationId
          ) {
            checkTextForSearchString(
              item.outcomesHelpText?.[lang],
              item,
              sectionIndex
            )
          }
        })
        options.forEach(option => {
          checkTextForSearchString(option.value, item, sectionIndex)
        })
        break
      case 'milestones':
        const { phases = [] } = item.typeProps
        phases.forEach(phase => {
          languages.forEach(lang => {
            if (
              phase?.endDateTooltip?.[lang] ||
              phase?.endDateTooltip?.portalTranslationId
            ) {
              checkTextForSearchString(
                phase.endDateTooltip?.[lang],
                item,
                sectionIndex
              )
            }
            if (phase?.label?.[lang] || phase?.label?.portalTranslationId) {
              checkTextForSearchString(phase.label?.[lang], item, sectionIndex)
            }
            if (
              phase?.startDateTooltip?.[lang] ||
              phase?.startDateTooltip?.portalTranslationId
            ) {
              checkTextForSearchString(
                phase.startDateTooltip?.[lang],
                item,
                sectionIndex
              )
            }
          })
        })
        break
      case 'table':
        const { columns = [] } = item.typeProps
        columns.forEach(column => {
          checkTextForSearchString(column.field, item, sectionIndex)
          languages.forEach(lang => {
            if (column?.label?.[lang] || column?.label?.portalTranslationId) {
              checkTextForSearchString(column.label?.[lang], item, sectionIndex)
            }
          })
        })
        break
      case 'associatedContactsList':
        const { roles = [] } = item.typeProps
        roles.forEach(role => {
          checkTextForSearchString(role.apiValue, item, sectionIndex)
          languages.forEach(lang => {
            if (role?.label?.[lang]) {
              checkTextForSearchString(role.label[lang], item, sectionIndex)
            }
          })
        })
        break
      case 'budget':
        const { expensesLines = [], revenuesLines = [] } = item.typeProps
        languages.forEach(lang => {
          if (
            item?.typeProps?.expensesHelpText?.[lang] ||
            item?.typeProps?.expensesHelpText?.portalTranslationId
          ) {
            checkTextForSearchString(
              item.typeProps.expensesHelpText[lang],
              item,
              sectionIndex
            )
          }
          if (
            item?.typeProps?.revenuesHelpText?.[lang] ||
            item?.typeProps?.revenuesHelpText?.portalTranslationId
          ) {
            checkTextForSearchString(
              item.typeProps.revenuesHelpText[lang],
              item,
              sectionIndex
            )
          }
        })
        expensesLines.forEach(line =>
          checkTextForSearchString(line, item, sectionIndex)
        )
        revenuesLines.forEach(line =>
          checkTextForSearchString(line, item, sectionIndex)
        )
        break

      case 'icce_budget':
        const { baseCategories = [] } = item.typeProps
        baseCategories.forEach(category => {
          languages.forEach(lang => {
            if (
              category?.title?.[lang] ||
              category?.title?.portalTranslationId
            ) {
              checkTextForSearchString(category.title[lang], item, sectionIndex)
            }
          })
        })
        break
      default:
        break
    }
  }

  //   checkTextForSearchString(data.titleEN)
  //   checkTextForSearchString(data.titleFR)

  //   if (data.pdfProps) {
  //     checkTextForSearchString(data.pdfProps?.header.textEN)
  //     checkTextForSearchString(data.pdfProps?.header.textFR)
  //     checkTextForSearchString(data.pdfProps?.footer.textEN)
  //     checkTextForSearchString(data.pdfProps?.footer.textFR)
  //   }

  data.sections.forEach((section, sectionIndex) => {
    languages.forEach(lang => {
      if (section?.title?.[lang]) {
        checkTextForSearchString(section.title[lang], {
          id: 'section' + sectionIndex
        })
      }
    })
    section.elements.forEach(element => {
      const loopElement = element => {
        if (element.elements) {
          checkElementForReferences(element, sectionIndex)
          element.elements.forEach(child => {
            loopElement(child)
          })
        } else {
          checkElementForReferences(element, sectionIndex)
        }
      }
      loopElement(element)
    })
  })
  return found
}

export default function SearchInFormEditor ({ scrollRef, data, goToSection }) {
  const [searchString, setSearchString] = useState('')
  const [result, setResult] = useState(null)
  const [resultIndex, setResultIndex] = useState(0)
  const [showBox, setShowBox] = useState(false)
  const { values } = useFormikContext()
  const { objectsConnected = [] } = values
  const objectIdToName = Object.fromEntries(
    objectsConnected.map(obj => [obj.identId, obj.name])
  )

  useEffect(() => {
    if (result && result[resultIndex]) {
      const { id, section } = result[resultIndex]
      goToSection(section)
      setTimeout(() => {
        const domElement = document.getElementById(id)
        scrollRef.current.scrollTop(domElement.offsetTop)
      }, 300)
    }
  }, [result, resultIndex])

  const keyDownCheck = e => {
    if (e.key === 'f' && e.ctrlKey) {
      e.preventDefault()
      setShowBox(!showBox)
    }
  }

  useEffect(() => {
    document.addEventListener('keydown', keyDownCheck)
    setResult(null)
    setSearchString('')
    return () => {
      document.removeEventListener('keydown', keyDownCheck)
    }
  }, [showBox])

  if (!showBox) {
    return null
  }

  return (
    <Card
      elevation={6}
      style={{
        position: 'fixed',
        top: 80,
        right: 30,
        zIndex: 100,
        padding: 8
      }}
    >
      <Grid container justifyContent='center' alignItems='center'>
        {result && (
          <>
            {result.length > 0
              ? (
                <>
                  <Grid item style={{ paddingLeft: 4, paddingRight: 4 }}>
                    {Number(resultIndex + 1) + ' '}
                    <Trans>of</Trans>
                    {' ' + result.length}
                  </Grid>
                  <Grid item>
                    <IconButton
                      size='small'
                      disabled={resultIndex === 0}
                      onClick={e => {
                        setResultIndex(resultIndex - 1)
                      }}
                    >
                      <Icon>arrow_upward</Icon>
                    </IconButton>
                  </Grid>
                  <Grid item style={{ paddingRight: 4 }}>
                    <IconButton
                      size='small'
                      disabled={resultIndex + 1 === result.length}
                      onClick={e => {
                        setResultIndex(resultIndex + 1)
                      }}
                    >
                      <Icon>arrow_downward</Icon>
                    </IconButton>
                  </Grid>
                </>
                )
              : (
                <span style={{ paddingRight: 8 }}>
                  <Trans>Nothing found!</Trans>
                </span>
                )}
          </>
        )}

        <Grid item xs>
          <TextField
            label={<Trans>Search form elements</Trans>}
            value={searchString}
            onChange={e => {
              setSearchString(e.target.value)
            }}
            variant='outlined'
            fullWidth
          />
        </Grid>
        <Grid item style={{ paddingLeft: 5 }}>
          <Button
            color='primary'
            variant='contained'
            disabled={!searchString}
            onClick={e => {
              const found = searchForOccurrence({
                data,
                searchString,
                objectIdToName
              })
              setResult(Object.values(found))
              setResultIndex(0)
            }}
          >
            <Trans>Search</Trans>
          </Button>
        </Grid>
        <Grid item style={{ paddingLeft: 4 }}>
          <IconButton
            size='small'
            onClick={e => {
              setShowBox(false)
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Grid>
      </Grid>
    </Card>
  )
}
